/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/
 
import { fetchGraphQl } from '@/quote-management/api';
import { state } from '@/quote-management/lib/state';
import { DELETE_QUOTE_MUTATION } from '@/quote-management/api/deleteQuote/graphql/DeleteQuoteMutation';
import { events } from '@adobe-commerce/event-bus';

export interface DeleteQuoteSuccessResult {
  __typename: 'NegotiableQuoteUidOperationSuccess';
  quoteUid: string;
}

export interface DeleteQuoteFailureError {
  __typename: string;
  message?: string;
  uid?: string;
}

export interface DeleteQuoteFailureResult {
  __typename: 'DeleteNegotiableQuoteOperationFailure';
  quoteUid: string;
  errors: DeleteQuoteFailureError[];
}

export interface DeleteQuoteOutput {
  resultStatus: string;
  operationResults: Array<DeleteQuoteSuccessResult | DeleteQuoteFailureResult>;
}

export const deleteQuote = async ( quoteUids: string[] | string ): Promise<DeleteQuoteOutput> => {
  if (!state.authenticated) {
    return Promise.reject(new Error('Unauthorized'));
  }

  const quoteUidsArray = Array.isArray(quoteUids) ? quoteUids : [quoteUids];

  try {
    const response: any = await fetchGraphQl(DELETE_QUOTE_MUTATION, {
      variables: {
        quoteUids: quoteUidsArray,
      },
    });

    const { errors } = response || {};
    if (errors && errors.length) {
      const errorMessages = errors.map((e: any) => e?.message).filter(Boolean).join('; ');
      throw new Error(errorMessages || 'Failed to delete negotiable quotes');
    }

    const raw = response?.data?.deleteNegotiableQuotes;
    if (!raw) {
      throw new Error('No delete result returned');
    }

    const transformed: DeleteQuoteOutput = {
      resultStatus: raw.result_status,
      operationResults: (raw.operation_results || []).map((result: any) => {
        const typename = result?.__typename;
        if (typename === 'NegotiableQuoteUidOperationSuccess') {
          const success: DeleteQuoteSuccessResult = {
            __typename: 'NegotiableQuoteUidOperationSuccess',
            quoteUid: result?.quote_uid,
          };
          return success;
        }
        const failure: DeleteQuoteFailureResult = {
          __typename: 'DeleteNegotiableQuoteOperationFailure',
          quoteUid: result?.quote_uid,
          errors: (result?.errors || []).map((err: any) => ({
            __typename: err?.__typename,
            message: err?.message,
            uid: err?.uid,
          })),
        };
        return failure;
      }),
    };

    // Emit event with successfully deleted quote UIDs
    const deletedUids = transformed.operationResults
      .filter((res) => res.__typename === 'NegotiableQuoteUidOperationSuccess')
      .map((res) => (res as DeleteQuoteSuccessResult).quoteUid);

    if (deletedUids.length > 0) {
      events.emit('quote-management/negotiable-quote-deleted', {
        deletedQuoteUids: deletedUids,
        resultStatus: transformed.resultStatus,
      });
    }

    return transformed;
  } catch (error) {
    // Emit error event for integration layer
    events.emit('quote-management/negotiable-quote-delete-error', {
      error: error instanceof Error ? error : new Error(String(error)),
      attemptedQuoteUids: quoteUidsArray,
    });
    return Promise.reject(error);
  }
};
